import { Countdown } from '@/components/countdown';
import { LeaderboardTable } from '@/components/leaderboard-table';
import { AirdropFaqModal } from '@/components/modals/airdrop-faq';
import { NavButton, NavButtonDirection } from '@/components/nav-button';
import { PageLayout } from '@/components/page-layout';
import { TooltipIcon } from '@/components/tooltip-icon';
import {
  AIRDROP_GUIDE_URL,
  AppRoute,
  INITIAL_CLAIM_MULTIPLIER,
  LeaderboardRecordCategory,
  SECONDS_IN_DAY,
  SECONDS_IN_HOUR,
} from '@/constants/shared';
import { useCurrentUserData } from '@/hooks/useCurrentUserData';
import { useLeaderboardData } from '@/hooks/useLeaderboard';
import {
  Box,
  Card,
  Flex,
  FormatNumber,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ComponentProps } from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import { LuClock3 } from 'react-icons/lu';
import { useSearchParams } from 'react-router';
import plumeTokenSvgPath from '/assets/home/plume-token.svg';
import xpPathSvg from '/assets/leaderboard/pp.svg';
import checkSquareSvgPath from '/assets/season/check-square.svg';
import goonSvgPath from '/assets/season/goon.svg';
import plumeSquareSvgPath from '/assets/season/plume-square.svg';
import rocketSvgPath from '/assets/season/rocket.svg';
import rocketBWSvgPath from '/assets/season/rocketBW.svg';
const AIRDROP_CLAIM_URL = 'https://claim.plume.org'; // TODO: change to claim.plumenetwork.xyz before launch
const STAKING_URL = 'https://staking.plume.org';

type TopCardProps = {
  headerLeft: React.ReactNode;
  buttonText: string;
  buttonHref: string;
  children: React.ReactNode;
  order?: number[];
  hideOnMobile?: boolean;
  buttonDirection?: NavButtonDirection;
  headerContainerProps?: ComponentProps<typeof Flex>;
};

type TopBannerProps = {
  title: string;
  description: string;
  buttonText: string;
  buttonHref: string;
  buttonShowExternalIcon?: boolean;
  backgroundImage?: string;
  backgroundColor?: string;
  bottomMiddle?: React.ReactNode;
  bottomRight?: React.ReactNode;
  order?: number[];
  titleImage?: string;
  h?: number | string;
  activatedMessage?: string;
};

const TopCard = ({
  headerLeft,
  children,
  buttonText,
  buttonHref,
  order,
  hideOnMobile,
  buttonDirection = 'none',
  headerContainerProps,
}: TopCardProps) => (
  <Card.Root
    flex={1}
    borderRadius="2xl"
    fontWeight="500"
    order={order}
    p={[6, 6, 8]}
    display={[hideOnMobile ? 'none' : 'flex', 'flex']}
  >
    <Card.Header p={0}>
      <Flex justifyContent="space-between" alignItems="center" fontFamily="Inter, sans-serif" {...headerContainerProps}>
        {headerLeft}
        <NavButton href={buttonHref} fontSize={['md', 'md', 'xs']} direction={buttonDirection}>
          {buttonText}
        </NavButton>
      </Flex>
    </Card.Header>
    <Card.Body alignItems="start" justifyContent="space-between" h="100%" p={0}>
      {children}
    </Card.Body>
  </Card.Root>
);

const TopBanner = ({
  title,
  description,
  buttonText,
  buttonHref,
  buttonShowExternalIcon,
  backgroundImage,
  backgroundColor,
  bottomMiddle,
  bottomRight,
  order,
  titleImage,
  activatedMessage,
  h = 240,
  titleProps,
  descriptionProps,
  shouldVerticalStackOnMobile = false,
  showBottomMiddleOnTopOnMobile = false,
  ...props
}: TopBannerProps &
  ComponentProps<typeof Card.Root> & {
    titleProps?: ComponentProps<typeof Heading>;
    descriptionProps?: ComponentProps<typeof Heading>;
    shouldVerticalStackOnMobile?: boolean;
    showBottomMiddleOnTopOnMobile?: boolean;
  }) => (
  <Card.Root
    flex={1}
    borderRadius="2xl"
    minH={h}
    fontWeight="500"
    {...(backgroundImage ? { bgImage: `url(${backgroundImage})` } : {})}
    {...(backgroundColor ? { backgroundColor } : {})}
    backgroundSize="cover"
    backgroundPosition="center"
    bgRepeat="no-repeat"
    w="100%"
    order={order}
    p={[6, 6, 8]}
    {...props}
  >
    <Card.Body h="100%" p={0}>
      <Flex direction="column" justify={'start'} h="100%">
        <VStack gap={2} alignItems="start" mb={4}>
          <Stack
            gap={3}
            direction={shouldVerticalStackOnMobile ? ['column', 'column', 'row'] : 'row'}
            alignItems={shouldVerticalStackOnMobile ? ['flex-start', 'flex-start', 'center'] : 'center'}
          >
            {titleImage ? <Image flexShrink={0} src={titleImage} boxSize={8} color="colorPalette.400" /> : null}
            <Heading fontWeight="700" fontSize="xl" color="fg.inverted" letterSpacing="-0.47px" {...titleProps}>
              {title}
            </Heading>
          </Stack>
          <Heading fontWeight="500" fontSize="lg" color="fg.inverted/70" letterSpacing={-0.33} {...descriptionProps}>
            {description}
          </Heading>
        </VStack>
        <Flex
          justify="space-between"
          align="center"
          w="100%"
          mt="auto"
          pb={activatedMessage ? 4 : 0}
          borderBottomWidth={'1px'}
          borderColor={activatedMessage ? 'border/10' : 'transparent'}
        >
          {/* Left group: button + bonus */}
          <Stack
            gap={4}
            direction={shouldVerticalStackOnMobile ? ['column', 'column', 'row'] : 'row'}
            alignItems={shouldVerticalStackOnMobile ? ['flex-start', 'flex-start', 'center'] : 'center'}
          >
            <NavButton
              href={buttonHref}
              fontSize={['md', 'md', 'xs']}
              bg="bg"
              p={5}
              direction={buttonShowExternalIcon ? 'up-right' : 'none'}
              linkProps={{ order: showBottomMiddleOnTopOnMobile ? [2, 2, 1] : [1, 1, 2] }}
            >
              {buttonText}
            </NavButton>
            <Box order={showBottomMiddleOnTopOnMobile ? [1, 1, 2] : [2, 2, 1]} w="full">
              {bottomMiddle}
            </Box>
          </Stack>
          {/* Right: Dismiss */}
          {bottomRight}
        </Flex>
        {activatedMessage && (
          <Text fontSize="sm" fontFamily="Inter, sans-serif" color="fg.inverted/70" mt={3}>
            {activatedMessage}
          </Text>
        )}
      </Flex>
    </Card.Body>
  </Card.Root>
);

type ClaimStatusItemProps = {
  title: string;
  titleTooltip?: string;
  lowerOverride?: React.ReactNode;
  value?: string | number;
  showTokenIcon?: boolean;
};

const ClaimStatusItem = ({ title, value, lowerOverride, showTokenIcon, titleTooltip }: ClaimStatusItemProps) => (
  <VStack alignItems="start" justifyContent="start" gap={1}>
    <HStack gap={2} color="fg.muted" alignItems="center" fontSize="sm" mb={2} fontFamily="Inter, sans-serif">
      <Text>{title}</Text>
      {titleTooltip && <TooltipIcon content={titleTooltip} />}
    </HStack>
    {lowerOverride ? (
      lowerOverride
    ) : (
      <>
        <HStack gap={2} justifyContent="center">
          <Text fontWeight="700" fontSize="xl" fontFamily="Lufga, sans-serif">
            {typeof value === 'number' ? <FormatNumber value={value} maximumFractionDigits={2} /> : value}
          </Text>
          {showTokenIcon && <Image src={plumeTokenSvgPath} boxSize={5} mt={-1} />}
        </HStack>
      </>
    )}
  </VStack>
);

export const SeasonOneClaim = () => {
  // an admin has the ability to view another user's airdrop page by passing in the walletAddress param
  const [searchParams] = useSearchParams();
  const walletAddress = searchParams.get('walletAddress');
  const { currentUserData, isLoading } = useCurrentUserData(walletAddress);

  const overrideDay1Override = searchParams.get('overrideDay1Override') === 'true';
  const doLeaderboardPreview = searchParams.get('preview') === 'true';

  const { leaderboard, latestRecord, fetchNextPage, hasNextPage, userOverride } = useLeaderboardData(
    LeaderboardRecordCategory.BattleGroup,
    walletAddress,
    doLeaderboardPreview,
    overrideDay1Override,
  );

  // Move TOP_RIGHT_CARDS inside the component
  const TOP_RIGHT_CARDS = {
    'initial-claim-deferred': (
      <TopBanner
        title="Claim 20% of your Base Allocation"
        description="To celebrate the launch of Plume Genesis, claim 20% of your base allocation now - our gift to you for believing in Plume and being with us from the very beginning."
        buttonText="Claim Now"
        buttonHref={AIRDROP_CLAIM_URL}
        buttonShowExternalIcon
        backgroundImage="/assets/season/claim-battle-airdrop-claim-bg.avif"
        order={[1, 1, 2]}
      />
    ),
    'initial-claim-claimed': (
      <TopBanner
        title="Claim 12% of your PLUME boost"
        description="You already chose to claim your base allocation — claim 12% of your PLUME boost now as our gift for joining us on the Plume Genesis journey."
        buttonText="Claim Now"
        buttonHref={AIRDROP_CLAIM_URL}
        buttonShowExternalIcon
        backgroundImage="/assets/season/claim-battle-airdrop-claim-bg.avif"
        order={[1, 1, 2]}
      />
    ),
    'won-bg': (
      <TopBanner
        title="Congrats - you won the claim party!"
        description="You ranked among the most active users.  Claim your Season 1 Airdrop now!"
        buttonText="Claim Now"
        buttonHref={AIRDROP_CLAIM_URL}
        buttonShowExternalIcon
        backgroundImage="/assets/season/claim-battle-won-bg.avif"
        order={[1, 1, 2]}
      />
    ),
    'all-unlocked': (
      <TopBanner
        title="Your Season 1 Airdrop is ready to claim"
        description="The wait is over. All airdrop allocations from Season 1 are now ready to claim!"
        buttonText="Claim Now"
        buttonHref={AIRDROP_CLAIM_URL}
        buttonShowExternalIcon
        backgroundImage="/assets/season/claim-battle-airdrop-claim-bg.avif"
        order={[1, 1, 2]}
      />
    ),
    'default-card': (
      <TopCard
        headerContainerProps={{ mb: 8 }}
        headerLeft={
          <Flex>
            <Image src={rocketSvgPath} boxSize={12} color="colorPalette.400" mr={4} />
          </Flex>
        }
        buttonText="Learn More"
        buttonHref={AIRDROP_GUIDE_URL}
        order={[1, 1, 2]}
        hideOnMobile
      >
        <Box>
          <Text fontWeight="700" fontSize="xl" fontFamily="Lufga, sans-serif">
            Claim Party
          </Text>
          <Text color="fg.muted" fontSize={['lg', 'md']} fontFamily="Inter, sans-serif" w="80%">
            Climb the leaderboard and gain early access to the airdrop through increased onchain activity
          </Text>
        </Box>
      </TopCard>
    ),
  };

  if (isLoading) return <Spinner />;
  if (!currentUserData) return <Box>Please log in to view your airdrop allocation</Box>;
  const { groupRemaining } = latestRecord ?? {};
  const { seasonOneAllocation } = currentUserData ?? {};
  const { battleGroupWonAt, calculatedTotal, calculatedBoost, calculatedAllocation, claimedBeforeMainnet, deferred } =
    seasonOneAllocation ?? {};
  const {
    initialClaimAmountTokens,
    initialClaimTimestamp,
    mainnetClaimAmountTokens,
    mainnetClaimTimestamp,
    claimBonusActivated,
    questBonusActivatedCount,
  } = seasonOneAllocation?.airdropClaim ?? {};

  const claimedSoFarTokens =
    (initialClaimAmountTokens || 0) + (mainnetClaimAmountTokens || 0) + (claimedBeforeMainnet || 0);
  const leftToClaimTokens = calculatedTotal - claimedSoFarTokens;
  const calculatedMainnetInitialAmount = (calculatedTotal - claimedBeforeMainnet) * INITIAL_CLAIM_MULTIPLIER;

  // epoch timestamp for the end of the bonus period (seconds)
  const bonusEndTimestamp = initialClaimTimestamp ? initialClaimTimestamp + 72 * SECONDS_IN_HOUR : null;

  const isWithin72HoursOfInitialClaim = bonusEndTimestamp && new Date().getTime() / 1000 < bonusEndTimestamp;

  // helper flag on each card if the bonus is activated
  const isStakingBonusActivated = claimBonusActivated;
  const isQuestBonusActivated = questBonusActivatedCount && questBonusActivatedCount > 0;

  const dayOneTimestamp = Number(import.meta.env.VITE_DAY_ONE_TIMESTAMP || '0');
  const bgEndDate = new Date((dayOneTimestamp + 90 * SECONDS_IN_DAY) * 1000);

  console.debug('Airdrop Claim', {
    seasonOneAllocation,
    isWithin72HoursOfInitialClaim,
    dayOneTimestamp,
    bonusEndTimestamp,
    isStakingBonusActivated,
    isQuestBonusActivated,
  });

  let topRightCard = TOP_RIGHT_CARDS['default-card'];

  /* PRIORITY ORDER:
  1. battle group is over, show all-unlocked
  2. user has won the battle group and not claimed yet, show won-bg
  3. user has not claimed the initial amount yet, show initial-claim
  4. else show default card (unless on mobile)

  Don't show the default card on mobile
  */

  if (new Date() > bgEndDate) {
    // battle group is over, show all-unlocked
    topRightCard = TOP_RIGHT_CARDS['all-unlocked'];
  } else if (battleGroupWonAt && !mainnetClaimTimestamp) {
    // user has won the battle group and not claimed yet, show won-bg
    topRightCard = TOP_RIGHT_CARDS['won-bg'];
  } else if (Boolean(calculatedMainnetInitialAmount) && !initialClaimTimestamp) {
    // user has not claimed the initial amount yet, show initial-claim
    if (deferred) {
      topRightCard = TOP_RIGHT_CARDS['initial-claim-deferred'];
    } else {
      topRightCard = TOP_RIGHT_CARDS['initial-claim-claimed'];
    }
  } else {
    // else show default card (unless mobile, then hide this card)
    topRightCard = TOP_RIGHT_CARDS['default-card'];
  }

  return (
    <PageLayout
      title="Plume's Airdrop Season 1"
      tertiaryTitle="Join the claim party and manage your airdrop rewards"
      slotRight={
        <Flex alignItems="center" gap={2}>
          <NavButton
            href={AIRDROP_CLAIM_URL}
            fontSize="sm"
            bg="bg"
            p={5}
            direction="up-right"
            fontWeight="500"
            letterSpacing="-0.09px"
          >
            Airdrop Claim
          </NavButton>
          <AirdropFaqModal rounded="full" fontSize="sm" />
        </Flex>
      }
    >
      <VStack w="full" alignItems="flex-start" gap={6}>
        <Flex flexDir={['column', 'column', 'row']} gap={6} w={'full'}>
          {/* Left card: Allocation */}
          <TopCard
            headerLeft={
              <Text color="fg.muted" fontSize="sm">
                Your Season 1 Allocation
              </Text>
            }
            buttonText="Guide"
            buttonHref={AIRDROP_GUIDE_URL}
            buttonDirection="up-right"
            order={[2, 2, 1]}
          >
            <Flex
              direction={['column', 'column', 'row']}
              justify="between"
              align="flex-start"
              h="100%"
              w="100%"
              gap={[8, 8, 0]}
              mt={[8, 8, 0]}
            >
              <Flex direction="column" justify="flex-end" align="flex-start" h="100%" w="100%">
                <VStack alignItems="flex-start" mr="auto">
                  <HStack gap={2}>
                    <Text fontWeight="700" fontSize="40px" fontFamily="Lufga, sans-serif" lineHeight="48px">
                      <FormatNumber value={calculatedAllocation} />
                    </Text>
                    <Image src={plumeTokenSvgPath} boxSize={[9, 9, 8]} mt={-1} />
                  </HStack>
                  <Text color="fg.muted" fontSize={['sm', 'md', 'sm']} fontFamily="Inter, sans-serif">
                    Base Allocation
                  </Text>
                </VStack>
              </Flex>
              <Box alignSelf="center" hideBelow="md">
                <Icon as={AiOutlinePlus} boxSize={6} color="fg.muted" />
              </Box>
              <Flex direction="column" justify="flex-end" align="flex-start" h="100%" w="100%">
                <VStack alignItems="flex-start" mx={[0, 0, 'auto']}>
                  <HStack gap={2}>
                    <Text fontWeight="700" fontSize="40px" fontFamily="Lufga, sans-serif" lineHeight="48px">
                      <FormatNumber value={calculatedBoost} />
                    </Text>
                    <Image src={plumeTokenSvgPath} boxSize={[9, 9, 8]} mt={-1} />
                  </HStack>
                  <Text color="fg.muted" fontSize={['sm', 'md', 'sm']} fontFamily="Inter, sans-serif">
                    PLUME Boost
                  </Text>
                </VStack>
              </Flex>
            </Flex>
            <VStack gap={6} w="full" alignItems="flex-start" hideFrom="md">
              <Box borderTopWidth="1px" borderColor="border" borderStyle="solid" mt={6} w="full" />
              <VStack w="full" alignItems="flex-start">
                <HStack justifyContent="space-between" h={12} w="full">
                  <Text fontWeight="500" color="text.secondary">
                    Claimed
                  </Text>
                  <Heading fontSize="lg" fontWeight="500" display="flex" alignItems="center" gap={2}>
                    <FormatNumber value={claimedSoFarTokens} maximumFractionDigits={2} />
                    <Image src={plumeTokenSvgPath} boxSize={6} />
                  </Heading>
                </HStack>
                <HStack justifyContent="space-between" h={12} w="full">
                  <Text fontWeight="500" color="text.secondary">
                    Remaining claim
                  </Text>
                  <Heading fontSize="lg" fontWeight="500" display="flex" alignItems="center" gap={2}>
                    <FormatNumber value={leftToClaimTokens} maximumFractionDigits={2} />
                    <Image src={plumeTokenSvgPath} boxSize={6} />
                  </Heading>
                </HStack>
                <HStack justifyContent="space-between" h={12} w="full">
                  <Text fontWeight="500" color="text.secondary">
                    Claim timeline 0 - 90 days
                  </Text>
                  <Box whiteSpace="nowrap" fontSize={'lg'} fontWeight="700" fontFamily="Lufga, sans-serif">
                    Accelerate with usage
                  </Box>
                </HStack>
              </VStack>
            </VStack>
          </TopCard>

          {/* Right card: Claim Battle */}
          {topRightCard}
        </Flex>
        {isWithin72HoursOfInitialClaim ? (
          <Flex direction={['column', 'column', 'row']} gap={6} w="full">
            <TopBanner
              h={'auto'}
              titleImage={plumeSquareSvgPath}
              title="Exclusive Plume Staking offer for you!"
              description="Help secure the chain by staking PLUME - do so within 72 hours and earn a 10x PP multiplier for 90 days up to the amount you just claimed."
              shouldVerticalStackOnMobile
              showBottomMiddleOnTopOnMobile
              buttonText="Stake PLUME"
              buttonHref={STAKING_URL}
              buttonShowExternalIcon
              backgroundColor="bg.darkBrown"
              bottomMiddle={
                <HStack color="text.primary" fontSize="sm" fontFamily="Inter, sans-serif" alignItems="center">
                  <Image src={xpPathSvg} boxSize={6} />
                  <Text color="fg.inverted">10x PP multiplier offer available for </Text>
                  <Countdown
                    fontWeight={500}
                    color="text.tertiary"
                    format="HM"
                    date={new Date(bonusEndTimestamp * 1000)}
                  />
                </HStack>
              }
              activatedMessage={isStakingBonusActivated ? 'Bonus offer has been activated' : undefined}
              titleProps={{ fontSize: 'lg' }}
              descriptionProps={{ fontSize: 'md', fontFamily: 'Inter, sans-serif' }}
              p={6}
            />
            <TopBanner
              h={'auto'}
              titleImage={checkSquareSvgPath}
              shouldVerticalStackOnMobile
              showBottomMiddleOnTopOnMobile
              title="Exclusive Level Up offer for you!"
              description="Learn about the Plume ecosystem and earn Plume Points - any quests you complete within the next 72 hours earn you a 10x PP multiplier"
              buttonText="Level Up"
              buttonHref={AppRoute.Quests}
              buttonShowExternalIcon
              backgroundColor="bg.darkBlue"
              bottomMiddle={
                <HStack color="text.primary" fontSize="sm" fontFamily="Inter, sans-serif" alignItems="center">
                  <Image src={xpPathSvg} boxSize={6} />
                  <Text color="fg.inverted">10x PP multiplier offer available for </Text>
                  <Countdown
                    fontWeight={500}
                    color="text.tertiary"
                    format="HM"
                    date={new Date(bonusEndTimestamp * 1000)}
                  />
                </HStack>
              }
              titleProps={{ fontSize: 'lg' }}
              descriptionProps={{ fontSize: 'md', fontFamily: 'Inter, sans-serif' }}
              activatedMessage={
                isQuestBonusActivated
                  ? `Bonus offer has been activated for ${questBonusActivatedCount} quest${
                      questBonusActivatedCount === 1 ? '' : 's'
                    }`
                  : undefined
              }
              p={6}
            />
          </Flex>
        ) : null}
        <Card.Root borderRadius="2xl" px={2} pt={1} pb={2} fontWeight="500" hideBelow="md" w="full">
          <Card.Body>
            <Flex justifyContent="space-between" alignItems="start" w="full">
              <ClaimStatusItem title="Claimed" value={claimedSoFarTokens} showTokenIcon />
              <ClaimStatusItem title="Remaining claim" value={leftToClaimTokens} showTokenIcon />
              <ClaimStatusItem
                title="Claim timeline 0 - 90 days"
                titleTooltip="Claim early by using Plume mainnet via the Plume Portal"
                lowerOverride={
                  <HStack whiteSpace="nowrap" fontWeight="700" fontFamily="Lufga, sans-serif">
                    <Text>Accelerate with usage</Text>
                    <Image src={rocketBWSvgPath} boxSize={6} />
                  </HStack>
                }
              />
              <Box></Box> {/* extra box for spacing */}
            </Flex>
          </Card.Body>
        </Card.Root>
      </VStack>

      <VStack gap={2} alignItems="flex-start">
        <Heading fontSize={'2xl'} fontWeight="700" lineHeight="32px" letterSpacing="-0.47px">
          Claim Party
        </Heading>
        <Heading color="fg.muted" fontSize="lg" fontWeight="500" lineHeight="24px">
          Each week, we reward the most active users in the group with early access to the airdrop claim
        </Heading>
      </VStack>
      {!battleGroupWonAt && (
        <SimpleGrid columns={[1, 1, 2]} gap={4} fontWeight="500">
          <Card.Root borderRadius="2xl">
            <Card.Body>
              <Image src={xpPathSvg} boxSize={8} mb={[5, null, 4]} />
              <Card.Title fontFamily="Lufga, sans-serif" mb={2} fontWeight="500" fontSize="lg">
                Earn Plume Points to climb the leaderboard
              </Card.Title>
              <Card.Description fontFamily="Inter, sans-serif" fontSize="md" letterSpacing={-0.18}>
                The more TVL, transactions and volume you generate, the more Plume Points you earn! Bonus PP rewards
                consistent activity.
              </Card.Description>

              <VStack gap={4} alignItems="flex-start" w="full" hideFrom="md">
                <Box borderTopWidth="1px" borderColor="border" borderStyle="solid" mt={6} w="full" />

                <HStack
                  gap={1}
                  height="100%"
                  alignItems="center"
                  justifyContent={['space-between', 'space-between', 'inherit']}
                  w="full"
                  h={12}
                >
                  <Image src={goonSvgPath} opacity={0.7} boxSize={8} mr={4} hideBelow="md" />
                  <Text fontWeight={[500, 500, 700]} fontFamily="Lufga, sans-serif" order={[2, 2, 1]} fontSize="lg">
                    {groupRemaining ?? 0}
                  </Text>
                  <Text color="fg.muted" ml={[0, null, 2]} fontFamily="Inter, sans-serif" order={[1, 1, 2]}>
                    Goons left in your group
                  </Text>
                </HStack>

                <HStack
                  gap={4}
                  height="100%"
                  alignItems="center"
                  justifyContent={['space-between', 'space-between', 'inherit']}
                  w="full"
                  h={12}
                >
                  <Icon as={LuClock3} boxSize={6} color="fg.muted" hideBelow="md" />
                  <Countdown
                    fontFamily="Lufga, sans-serif"
                    order={[2, 2, 1]}
                    fontSize="lg"
                    fontWeight={[500, 500, 700]}
                  />
                  <Text color="fg.muted" fontFamily="Inter, sans-serif" order={[1, 1, 2]}>
                    Leaderboard updates{' '}
                    <Text as="span" hideBelow="md">
                      daily at midnight UTC
                    </Text>
                    <Text as="span" hideFrom="md">
                      in
                    </Text>
                  </Text>
                </HStack>
              </VStack>
            </Card.Body>
          </Card.Root>
          <Box hideBelow="md">
            <SimpleGrid columns={1} gap={4} height="100%">
              <Card.Root borderRadius="2xl" flex={1}>
                <Card.Body height="100%">
                  <HStack
                    gap={1}
                    height="100%"
                    alignItems="center"
                    justifyContent={['space-between', 'space-between', 'inherit']}
                  >
                    <Image src={goonSvgPath} opacity={0.7} boxSize={8} mr={4} hideBelow="md" />
                    <Text
                      fontWeight={[500, 500, 700]}
                      fontFamily="Lufga, sans-serif"
                      order={[2, 2, 1]}
                      fontSize={['xl', 'xl', 'md']}
                    >
                      {groupRemaining ?? 0}
                    </Text>
                    <Text color="fg.muted" ml={2} fontFamily="Inter, sans-serif" order={[1, 1, 2]}>
                      Goons left in your group
                    </Text>
                  </HStack>
                </Card.Body>
              </Card.Root>
              <Card.Root borderRadius="2xl" flex={1}>
                <Card.Body height="100%">
                  <HStack
                    gap={1}
                    height="100%"
                    alignItems="center"
                    justifyContent={['space-between', 'space-between', 'inherit']}
                  >
                    <Icon as={LuClock3} boxSize={6} color="fg.muted" mr={4} hideBelow="md" />
                    <Countdown
                      fontFamily="Lufga, sans-serif"
                      order={[2, 2, 1]}
                      fontSize={['xl', 'xl', 'md']}
                      fontWeight={[500, 500, 700]}
                    />
                    <Text color="fg.muted" fontFamily="Inter, sans-serif" order={[1, 1, 2]}>
                      Leaderboard updates{' '}
                      <Text as="span" hideBelow="md">
                        daily at midnight UTC
                      </Text>
                      <Text as="span" hideFrom="md">
                        in
                      </Text>
                    </Text>
                  </HStack>
                </Card.Body>
              </Card.Root>
            </SimpleGrid>
          </Box>
        </SimpleGrid>
      )}
      {leaderboard && (
        <LeaderboardTable
          leaderboard={leaderboard}
          recordType={LeaderboardRecordCategory.BattleGroup}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          userOverride={userOverride}
          currentUserData={currentUserData}
          isLoading={isLoading}
        />
      )}
    </PageLayout>
  );
};
